import { useNotification } from '@refinedev/core'
import { Form, FormProps, Input, Modal, ModalProps } from 'antd'
import { useCallback, useState } from 'react'
import { useAddBank } from '../../../api/bank'
import {
  AutoCompleteCountry,
  Option,
} from '../../../components/inputs/autocomplete/autocompleteCountry'
import { IBank } from '../../../interfaces/banks'
import { ButtonListFooterModalSM } from '../../../pages/components/buttonsList'
import { confirmModalUnsavedChangesProps } from '../../../pages/components/constants'
import {
  bankAddressRules,
  bankNameRules,
  bankSwiftRules,
  intermediaryAddressRules,
  intermediaryBankNameRules,
  intermediarySwiftRules,
} from '../../../pages/payment-request/addBank.constants'
import {
  companyTelRules as bankTelRules,
  companyTinVatUidRules as bankTinVatUidRules,
} from '../../../pages/payment-request/addCompany.constants'
import { mapCountry } from '../../../utils/edit.constants'
import { useNotificationOkCancel } from '../../../utils/hooks/useNotificationOkCancel.hook'
import { DownOutlined } from '@ant-design/icons'

interface IProps {
  formProps: FormProps
  modalProps: ModalProps
  formIsLoading: boolean
  formSubmit: (data: any) => void
  closeModal: () => void
  refetchList: (createdBank?: any) => void
  customerId?: number
  isCustomer?: boolean
}

export const NewBankModalCreate = ({
  modalProps,
  formProps,
  closeModal,
  refetchList,
  customerId,
  isCustomer = false,
}: IProps) => {
  const { open: openNotificationSucces } = useNotification()
  const [countryData, setCountryData] = useState<any>()

  const { mutate: addBank, isLoading } = useAddBank({
    onSuccess: (response: any) => {
      if (openNotificationSucces) {
        openNotificationSucces({
          key: 'success_transaction_confirm',
          message: 'Create bank successfully!',
          type: 'success',
        })
      }
      const createdBank =
        response?.data?.data ??
        response?.data?.item ??
        response?.data
      formProps.form?.resetFields()
      closeModal()
      refetchList(createdBank)
    },
    onError: (error: { message: string; reason_code: string }) => {
      if (openNotificationSucces) {
        openNotificationSucces({
          key: 'error',
          message: error?.message || 'Create bank failed!',
          type: 'error',
        })
      }
    },
  })

  const onSubmit = useCallback(
    (formValues: IBank) => {
      const data = {
        name: formValues?.name,
        swift: formValues?.swift,
        legalAddress: formValues?.legalAddress,
        country: countryData?.value,
        tin: formValues?.tin,
        phoneNumber1: formValues?.phoneNumber1,
        intermediaryBank: formValues?.intermediaryBank,
        intermediarySwift: formValues?.intermediarySwift,
        intermediaryAddress: formValues?.intermediaryAddress,
        ...(customerId && { userId: customerId }),
      }
      addBank(data)
    },
    [addBank, countryData?.value, customerId]
  )

  const handleSubmit = useCallback(() => {
    formProps.form?.submit()
  }, [formProps.form])

  const { openNotification, contextHolder } = useNotificationOkCancel({
    ...confirmModalUnsavedChangesProps,
    onOk: closeModal,
  })

  const onModalCancel = useCallback(() => {
    const formIsNotEmpty = Object.values(formProps.form?.getFieldsValue()).some(
      (value) => value !== undefined
    )
    if (formIsNotEmpty) {
      openNotification()
    } else {
      closeModal()
    }
  }, [closeModal, formProps.form, openNotification])

  const afterClose = useCallback(() => {
    formProps.form?.resetFields()
  }, [formProps])

  const changeCountryData = useCallback(
    (data: any) => {
      if (data.id !== countryData?.id) {
        setCountryData(data)
      }
    },
    [countryData?.id]
  )

  const onSelectCountry = useCallback(
    (_: any, { data }: Option<any>) => {
      return changeCountryData(data)
    },
    [changeCountryData]
  )

  const clearCountryData = useCallback(() => {
    setCountryData(undefined)
  }, [])

  return (
    <>
      <Modal
        {...modalProps}
        title='Add new bank'
        className='smartModalSM'
        footer={ButtonListFooterModalSM(handleSubmit, onModalCancel, isLoading)}
        afterClose={afterClose}
        onCancel={onModalCancel}>
        {contextHolder}
        <Form
          {...formProps}
          layout='vertical'
          onFinish={onSubmit}
          disabled={isLoading}>
          <Form.Item name='name' rules={bankNameRules}>
            <Input
              placeholder='Bank name'
              size='large'
              style={{
                height: '35px',
              }}
            />
          </Form.Item>
          <Form.Item name='swift' rules={bankSwiftRules}>
            <Input
              placeholder='SWIFT'
              size='large'
              style={{
                height: '35px',
              }}
            />
          </Form.Item>
          <Form.Item name='legalAddress' rules={bankAddressRules}>
            <Input
              placeholder='Bank address'
              size='large'
              style={{
                height: '35px',
              }}
            />
          </Form.Item>
          {isCustomer ? (
            <Form.Item
              name='country'
              rules={[
                { required: true, message: 'Please enter bank country' },
              ]}>
              <AutoCompleteCountry
                resource='api/v1/worldCheck/countries'
                mapItem={mapCountry}
                onSelect={onSelectCountry}
                allowClear
                refetchOnEmptyValue
                onClear={clearCountryData}>
                <Input
                  // style={{ fontSize: 16 }}
                  placeholder='Country of incorporation'
                  suffix={<DownOutlined />}
                />
              </AutoCompleteCountry>
            </Form.Item>
          ) : (
            <>
              <Form.Item
                name='country'
                rules={[
                  { required: true, message: 'Please enter bank country' },
                ]}>
                <AutoCompleteCountry
                  resource='api/v1/worldCheck/countries'
                  mapItem={mapCountry}
                  onSelect={onSelectCountry}
                  allowClear
                  refetchOnEmptyValue
                  onClear={clearCountryData}>
                  <Input
                    // style={{ fontSize: 16 }}
                    placeholder='Country of incorporation'
                    suffix={<DownOutlined />}
                  />
                </AutoCompleteCountry>
              </Form.Item>
              <Form.Item name='tin' rules={bankTinVatUidRules}>
                <Input
                  placeholder='TIN / RegNo'
                  size='large'
                  style={{
                    height: '35px',
                  }}
                />
              </Form.Item>
              <Form.Item name='phoneNumber1' rules={bankTelRules}>
                <Input
                  placeholder='Tel'
                  size='large'
                  style={{
                    height: '35px',
                  }}
                />
              </Form.Item>
              <Form.Item
                name='intermediaryBank'
                rules={intermediaryBankNameRules}>
                <Input
                  placeholder='intermediary Bank name'
                  size='large'
                  style={{
                    height: '35px',
                  }}
                />
              </Form.Item>
              <Form.Item
                name='intermediarySwift'
                rules={intermediarySwiftRules}>
                <Input
                  placeholder='Intermediary SWIFT'
                  size='large'
                  style={{
                    height: '35px',
                  }}
                />
              </Form.Item>
              <Form.Item
                name='intermediaryAddress'
                rules={intermediaryAddressRules}>
                <Input
                  placeholder='Intermediary: City/Country'
                  size='large'
                  style={{
                    height: '35px',
                  }}
                />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </>
  )
}
